
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, InputAdornment, Chip,
    Table, TableBody, TableCell, TableHead,
    TableRow, IconButton, Menu, MenuItem, Tooltip,
    TablePagination, CircularProgress, Button, Dialog,
    DialogTitle, DialogContent, DialogActions,
    Select, FormControl,
} from '@mui/material';
import {
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    Visibility as ViewIcon,
    Cancel as CancelIcon,
    Inventory2 as EmptyIcon,
    ReceiptLong as ReceiptIcon,
    CalendarToday as CalendarIcon,
    LocalShipping as ShipIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../services/api';
import { StatusChip, STATUS_CONFIG } from './CustomerOrderlist';

// ── Shared cell sx ─────────────────────────────────────────────────────────────
const headCellSx = (extra = {}) => ({
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    borderBottom: '1px solid rgba(188,150,94,0.15)',
    background: 'rgba(188,150,94,0.04)',
    py: 1.25,
    px: 1.5,
    whiteSpace: 'nowrap',
    ...extra,
});

const bodyCellSx = (extra = {}) => ({
    color: '#ffffff',
    fontSize: '0.82rem',
    borderBottom: '1px solid rgba(188,150,94,0.07)',
    py: 1.25,
    px: 1.5,
    ...extra,
});



// ── Actions Menu ───────────────────────────────────────────────────────────────
const ActionsMenu = ({ order, onView, onCancel, onShip }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const canCancel = !['Delivered', 'Cancelled'].includes(order.OrderStatus);
    const canShip = !['Shipped', 'Out For Delivery', 'Delivered', 'Cancelled'].includes(order.OrderStatus);

    return (
        <>
            <Tooltip title="Actions">
                <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                    sx={{ color: 'rgba(188,150,94,0.7)', border: '1px solid rgba(188,150,94,0.2)', borderRadius: 1.5, width: 28, height: 28, '&:hover': { background: 'rgba(188,150,94,0.1)', color: '#bc965e' } }}>
                    <MoreVertIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ sx: { background: '#1a1510', border: '1px solid rgba(188,150,94,0.2)', borderRadius: 2, minWidth: 160, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', mt: 0.5 } }}>

                <MenuItem onClick={() => { setAnchorEl(null); onView(order); }}
                    sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', gap: 1, '&:hover': { background: 'rgba(188,150,94,0.1)', color: '#bc965e' } }}>
                    <ViewIcon sx={{ fontSize: 15 }} /> View Details
                </MenuItem>

                {canShip && (
                    <MenuItem onClick={() => { setAnchorEl(null); onShip(order); }}
                        sx={{ color: '#64b5f6', fontSize: '0.82rem', gap: 1, '&:hover': { background: 'rgba(100,181,246,0.1)' } }}>
                        <ShipIcon sx={{ fontSize: 15 }} /> Ship Order
                    </MenuItem>
                )}

                {canCancel && (
                    <MenuItem onClick={() => { setAnchorEl(null); onCancel(order); }}
                        sx={{ color: '#ef4444', fontSize: '0.82rem', gap: 1, '&:hover': { background: 'rgba(239,68,68,0.1)' } }}>
                        <CancelIcon sx={{ fontSize: 15 }} /> Cancel Order
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const AdminOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [cancelOrder, setCancelOrder] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [shipOrder, setShipOrder] = useState(null);
    const [shipping, setShipping] = useState(false);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page + 1, limit: rowsPerPage,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter }),
                ...(dateFrom && { dateFrom }),
                ...(dateTo && { dateTo }),
            });
            const { data } = await API.get(`/admin/orders?${params}`);
            if (data.success) { setOrders(data.data); setTotal(data.total); }
        } catch { toast.error('Failed to load orders'); }
        finally { setLoading(false); }
    }, [page, rowsPerPage, search, statusFilter, dateFrom, dateTo]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const handleCancelConfirm = async () => {
        if (!cancelOrder) return;
        setCancelling(true);
        try {
            const { data } = await API.post(`/admin/orders/${cancelOrder.BillId}/cancel`);
            if (data.success) { toast.success('Order cancelled'); setCancelOrder(null); fetchOrders(); }
            else toast.error(data.message);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to cancel order');
        } finally { setCancelling(false); }
    };

    const handleShipConfirm = async () => {
        if (!shipOrder) return;
        setShipping(true);
        try {
            const { data } = await API.post(`/admin/orders/${shipOrder.BillId}/ship`);
            if (data.success) {
                toast.success(`Order shipped! AWB: ${data.awbCode}`);
                setShipOrder(null);
                fetchOrders();
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to ship order');
        } finally { setShipping(false); }
    };

    const hasFilters = search || statusFilter || dateFrom || dateTo;
    const STICKY_BG_HOVER = '#191410';

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>

            {/* ── Header ── */}
            <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ReceiptIcon sx={{ color: '#bc965e', fontSize: { xs: 22, md: 28 } }} />
                <Box>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '1rem', md: '1.35rem' } }}>
                        All Orders
                    </Typography>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: { xs: '0.72rem', md: '0.82rem' } }}>
                        {total} total orders
                    </Typography>
                </Box>
            </Box>

            {/* ── Filter bar ── */}
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <TextField
                    size="small"
                    placeholder="Search order, customer, phone…"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 17 }} />
                                </InputAdornment>
                            ),
                            sx: { color: 'white', fontSize: '0.82rem' },
                        },
                    }}
                    sx={{
                        flex: { xs: '1 1 100%', sm: '1 1 200px' },
                        '& .MuiOutlinedInput-root': {
                            background: 'rgba(188,150,94,0.05)', borderRadius: 2,
                            '& fieldset': { borderColor: 'rgba(188, 150, 94, 0.75)' },
                            '&:hover fieldset': { borderColor: 'rgba(188,150,94,0.5)' },
                            '&.Mui-focused fieldset': { borderColor: 'rgba(188,150,94,0.6)' },
                        },
                    }}
                />

                <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 140px' } }}>
                    <Select
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                        displayEmpty
                        sx={{
                            color: statusFilter ? STATUS_CONFIG[statusFilter]?.color : 'rgba(255,255,255,0.5)',
                            background: 'rgba(188,150,94,0.05)', borderRadius: 2, fontSize: '0.82rem',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(188, 150, 94, 0.67)' },
                            '& .MuiSvgIcon-root': { color: '#bc965e' },
                        }}
                        MenuProps={{ PaperProps: { sx: { background: '#1a1510', border: '1px solid rgba(188,150,94,0.2)' } } }}
                    >
                        <MenuItem value="" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>All Status</MenuItem>
                        {Object.keys(STATUS_CONFIG).map(s => (
                            <MenuItem key={s} value={s} sx={{ color: STATUS_CONFIG[s].color, fontSize: '0.82rem' }}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small" type="date" label="From"
                    value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setPage(0); }}
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CalendarIcon sx={{ color: '#bc965e', fontSize: 15, pointerEvents: 'none' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                color: 'white', fontSize: '0.8rem', position: 'relative',
                                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    opacity: 0, position: 'absolute', inset: 0,
                                    width: '100%', height: '100%', cursor: 'pointer', zIndex: 1,
                                },
                            },
                        },
                    }}
                    sx={{
                        flex: { xs: '1 1 100%', sm: '0 0 138px' },
                        '& .MuiOutlinedInput-root': { background: 'rgba(13,12,10,0.05)', borderRadius: 2, '& fieldset': { borderColor: 'rgba(188, 150, 94, 0.77)' } },
                        '& .MuiInputLabel-root': { color: '#fffffff', fontSize: '0.78rem' },
                    }}
                />

                <TextField
                    size="small" type="date" label="To"
                    value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setPage(0); }}
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CalendarIcon sx={{ color: '#bc965e', fontSize: 15, pointerEvents: 'none' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                color: 'white', fontSize: '0.8rem', position: 'relative',
                                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    opacity: 0, position: 'absolute', inset: 0,
                                    width: '100%', height: '100%', cursor: 'pointer', zIndex: 1,
                                },
                            },
                        },
                    }}
                    sx={{
                        flex: { xs: '1 1 100%', sm: '0 0 138px' },
                        '& .MuiOutlinedInput-root': { background: 'rgba(188,150,94,0.05)', borderRadius: 2, '& fieldset': { borderColor: 'rgba(188, 150, 94, 0.76)' } },
                        '& .MuiInputLabel-root': { color: '#ffffff', fontSize: '0.78rem' },
                    }}
                />

                {hasFilters && (
                    <Button
                        size="small"
                        onClick={() => { setSearchInput(''); setSearch(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); setPage(0); }}
                        sx={{
                            flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                            color: 'rgba(255,100,100,0.8)', textTransform: 'none',
                            border: '1px solid rgba(255,100,100,0.25)', borderRadius: 2,
                            fontSize: '0.78rem', px: 1.5, whiteSpace: 'nowrap',
                        }}
                    >
                        Clear filters
                    </Button>
                )}
            </Box>

            {/* ── Table card ── */}
            <Box
                className="glass-content-container"
                sx={{ borderRadius: 2, border: '1px solid rgba(188,150,94,0.15)', overflow: 'hidden', p: 0 }}
            >
                {loading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress size={36} sx={{ color: '#bc965e' }} />
                    </Box>
                ) : orders.length === 0 ? (
                    <Box textAlign="center" py={6}>
                        <EmptyIcon sx={{ fontSize: 48, color: 'rgba(188,150,94,0.2)', mb: 1.5 }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>No orders found</Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            className="admin-orders-table-wrapper"
                            sx={{
                                '&::-webkit-scrollbar': { height: 5 },
                                '&::-webkit-scrollbar-track': { background: 'rgba(188,150,94,0.06)' },
                                '&::-webkit-scrollbar-thumb': { background: 'rgba(188,150,94,0.4)', borderRadius: 3 },
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0,
                                    width: '717px', minWidth: '627px',
                                }}
                            >
                                <colgroup>
                                    <col style={{ width: '70px' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '150px' }} />
                                    <col style={{ width: '60px' }} />
                                    <col style={{ width: '110px' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '95px' }} />
                                    <col style={{ width: '52px' }} />
                                </colgroup>

                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headCellSx({ textAlign: 'center' })}>Order</TableCell>
                                        <TableCell sx={headCellSx({ textAlign: 'center' })}>Date</TableCell>
                                        <TableCell sx={headCellSx({ textAlign: 'left' })}>Customer</TableCell>
                                        <TableCell sx={headCellSx({ textAlign: 'center' })}>Items</TableCell>
                                        <TableCell sx={headCellSx({ textAlign: 'center' })}>Status</TableCell>
                                        <TableCell sx={headCellSx({ textAlign: 'center' })}>Payment</TableCell>
                                        <TableCell sx={headCellSx({ textAlign: 'right' })}>Amount</TableCell>
                                        <TableCell sx={headCellSx({ textAlign: 'center' })}>Action</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {orders.map(order => {
                                        const dateStr = new Date(order.CreatedAt).toLocaleDateString('en-IN', {
                                            day: '2-digit', month: 'short', year: '2-digit',
                                        });
                                        const status = order.OrderStatus || 'Pending';

                                        return (
                                            <TableRow
                                                key={order.BillId}
                                                onClick={() => navigate(`/admin/orders/${order.BillId}`)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': { background: 'rgba(188,150,94,0.05)' },
                                                    '&:hover .actions-sticky': { background: STICKY_BG_HOVER },
                                                    '&:last-child td': { borderBottom: 'none' },
                                                }}
                                            >
                                                <TableCell sx={bodyCellSx({ textAlign: 'center' })}>
                                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#bc965e', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        #{order.BillId}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={bodyCellSx({ textAlign: 'center' })}>
                                                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.88)', whiteSpace: 'nowrap' }}>
                                                        {dateStr}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={bodyCellSx({ textAlign: 'left' })}>
                                                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.88)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {order.CustomerName || '—'}
                                                    </Typography>
                                                    {order.PhoneNumber && (
                                                        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.77)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {order.PhoneNumber}
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                <TableCell sx={bodyCellSx({ textAlign: 'center', })}>
                                                    <Chip label={`${order.ItemCount}`} size="small"
                                                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, background: 'rgba(188,150,94,0.1)', border: '1px solid rgba(188,150,94,0.2)', color: '#ffffff' }} />
                                                </TableCell>

                                                <TableCell sx={bodyCellSx({ textAlign: 'center' })}>
                                                    <StatusChip status={status} />
                                                </TableCell>

                                                <TableCell sx={bodyCellSx({ textAlign: 'center' })}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>
                                                            {order.payment_method === 'cod' ? '💵 COD' : '💳 Online'}
                                                        </Typography>
                                                        <Chip
                                                            label={order.payment_status === 'paid' ? 'Paid' : 'Pending'}
                                                            size="small"
                                                            sx={{
                                                                height: 16, fontSize: '0.6rem', fontWeight: 700,
                                                                backgroundColor: order.payment_status === 'paid' ? 'rgba(76,175,80,0.12)' : 'rgba(255,152,0,0.12)',
                                                                border: `1px solid ${order.payment_status === 'paid' ? 'rgba(76,175,80,0.35)' : 'rgba(255,152,0,0.35)'}`,
                                                                color: order.payment_status === 'paid' ? '#4caf50' : '#ff9800',
                                                            }}
                                                        />
                                                    </Box>
                                                </TableCell>

                                                <TableCell sx={bodyCellSx({ textAlign: 'right' })}>
                                                    <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                        ₹{parseFloat(order.TotalAmount || 0).toFixed(2)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell
                                                    onClick={e => e.stopPropagation()}
                                                    sx={bodyCellSx({ textAlign: 'center', px: 0.75 })}
                                                >
                                                    <ActionsMenu
                                                        order={order}
                                                        onView={o => navigate(`/admin/orders/${o.BillId}`)}
                                                        onCancel={o => setCancelOrder(o)}
                                                        onShip={o => setShipOrder(o)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>

                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                            sx={{
                                color: 'rgba(255,255,255,0.5)',
                                borderTop: '1px solid rgba(188,150,94,0.1)',
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' },
                                '.MuiTablePagination-select': { color: '#bc965e' },
                                '.MuiTablePagination-actions button': { color: 'rgba(188,150,94,0.8)', '&:hover': { background: 'rgba(188,150,94,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' } },
                                '.MuiTablePagination-toolbar': { px: { xs: 1.5, sm: 2 }, minHeight: 48 },
                            }}
                        />
                    </>
                )}
            </Box>

            

            {/* ── Cancel Dialog ── */}
            <Dialog open={!!cancelOrder} onClose={() => setCancelOrder(null)}
                PaperProps={{ sx: { background: '#1a1510', border: '1px solid rgba(188,150,94,0.2)', borderRadius: 2, minWidth: 290, maxWidth: '90vw' } }}>
                <DialogTitle sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', pb: 1 }}>Cancel Order?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>
                        Cancel order <strong style={{ color: '#bc965e' }}>#{cancelOrder?.BillId}</strong>? Stock will be restored.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setCancelOrder(null)} size="small"
                        sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1.5, fontSize: '0.8rem' }}>
                        Go Back
                    </Button>
                    <Button onClick={handleCancelConfirm} disabled={cancelling} size="small"
                        sx={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', textTransform: 'none', borderRadius: 1.5, fontWeight: 700, fontSize: '0.8rem', px: 2, '&:hover': { background: 'rgba(239,68,68,0.25)' }, '&:disabled': { opacity: 0.5 } }}>
                        {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Ship Dialog ── */}
            <Dialog open={!!shipOrder} onClose={() => !shipping && setShipOrder(null)}
                PaperProps={{ sx: { background: '#1a1510', border: '1px solid rgba(100,181,246,0.2)', borderRadius: 2, minWidth: 290, maxWidth: '90vw' } }}>
                <DialogTitle sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', pb: 1 }}>
                    Ship Order?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>
                        This will create a Shiprocket shipment and generate AWB for order{' '}
                        <strong style={{ color: '#64b5f6' }}>#{shipOrder?.BillId}</strong>.
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', mt: 1 }}>
                        Status will be updated to <strong>Shipped</strong> automatically.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setShipOrder(null)}
                        disabled={shipping}
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1.5, fontSize: '0.8rem' }}>
                        Go Back
                    </Button>
                    <Button
                        onClick={handleShipConfirm}
                        disabled={shipping}
                        size="small"
                        startIcon={shipping ? <CircularProgress size={12} color="inherit" /> : <ShipIcon sx={{ fontSize: 14 }} />}
                        sx={{ background: 'rgba(100,181,246,0.15)', border: '1px solid rgba(100,181,246,0.4)', color: '#64b5f6', textTransform: 'none', borderRadius: 1.5, fontWeight: 700, fontSize: '0.8rem', px: 2, '&:hover': { background: 'rgba(100,181,246,0.25)' }, '&:disabled': { opacity: 0.5 } }}>
                        {shipping ? 'Shipping…' : 'Confirm Ship'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default AdminOrders;